import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ArbitrageOpportunity, OpportunitySnapshot } from '../../types/opportunity';
import { makeOpportunityKey } from '../../types/opportunity';

interface SnapshotsState {
  snapshots: { [key: string]: OpportunitySnapshot };
  lastUpdate: number;
}

const initialState: SnapshotsState = {
  snapshots: {},
  lastUpdate: 0
};

// Load snapshots from localStorage on initialization
const loadSnapshots = (): { [key: string]: OpportunitySnapshot } => {
  try {
    const saved = localStorage.getItem('arbitrage-snapshots');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convert back to snapshot objects
      return parsed || {};
    }
  } catch (error) {
    console.error('Failed to load snapshots from localStorage:', error);
  }
  return {};
};

// Save snapshots to localStorage
const saveSnapshots = (snapshots: { [key: string]: OpportunitySnapshot }) => {
  try {
    localStorage.setItem('arbitrage-snapshots', JSON.stringify(snapshots));
  } catch (error) {
    console.error('Failed to save snapshots to localStorage:', error);
  }
};

const snapshotsSlice = createSlice({
  name: 'snapshots',
  initialState: {
    ...initialState,
    snapshots: loadSnapshots()
  },
  reducers: {
    // Star an opportunity (create snapshot)
    starOpportunity: (state, action: PayloadAction<ArbitrageOpportunity>) => {
      const key = makeOpportunityKey(action.payload);
      // Store the opportunity with all its data, ensuring arrays are preserved
      const opportunityToStore = {
        ...action.payload,
        // Ensure we have valid arrays
        buy_levels: action.payload.buy_levels || [],
        sell_levels: action.payload.sell_levels || []
      };
      state.snapshots[key] = {
        opportunity: opportunityToStore,
        snapshotTimestamp: Date.now(),
        isStarred: true,
        lastSeenTimestamp: Date.now(),
        isDeleted: false
      };
      state.lastUpdate = Date.now();
      saveSnapshots(state.snapshots);
    },
    
    // Unstar an opportunity (remove snapshot)
    unstarOpportunity: (state, action: PayloadAction<string>) => {
      delete state.snapshots[action.payload];
      state.lastUpdate = Date.now();
      saveSnapshots(state.snapshots);
    },
    
    // Update last seen timestamp for a snapshot
    updateSnapshotLastSeen: (state, action: PayloadAction<string>) => {
      if (state.snapshots[action.payload]) {
        state.snapshots[action.payload].lastSeenTimestamp = Date.now();
        state.snapshots[action.payload].isDeleted = false;
      }
    },
    
    // Mark snapshot as deleted (opportunity no longer exists)
    markSnapshotDeleted: (state, action: PayloadAction<string>) => {
      if (state.snapshots[action.payload]) {
        state.snapshots[action.payload].isDeleted = true;
      }
    },
    
    // Batch update snapshot statuses based on current opportunities
    updateSnapshotStatuses: (state, action: PayloadAction<string[]>) => {
      const currentKeys = new Set(action.payload);
      let hasChanges = false;
      
      // Update all snapshots
      Object.keys(state.snapshots).forEach(key => {
        const snapshot = state.snapshots[key];
        const existsNow = currentKeys.has(key);
        const wasDeleted = snapshot.isDeleted;
        
        if (existsNow && !wasDeleted) {
          // Still exists and wasn't deleted - no change needed
          // Don't update lastSeenTimestamp to avoid unnecessary re-renders
          return;
        } else if (existsNow && wasDeleted) {
          // Was deleted but now exists again
          snapshot.lastSeenTimestamp = Date.now();
          snapshot.isDeleted = false;
          hasChanges = true;
        } else if (!existsNow && !wasDeleted) {
          // Just got deleted
          snapshot.isDeleted = true;
          hasChanges = true;
        }
        // If !existsNow && wasDeleted, no change needed
      });
      
      // Only update if there were actual changes
      if (hasChanges) {
        state.lastUpdate = Date.now();
        saveSnapshots(state.snapshots);
      }
    },
    
    // Clear all snapshots
    clearSnapshots: (state) => {
      state.snapshots = {};
      state.lastUpdate = Date.now();
      saveSnapshots(state.snapshots);
    }
  }
});

export const {
  starOpportunity,
  unstarOpportunity,
  updateSnapshotLastSeen,
  markSnapshotDeleted,
  updateSnapshotStatuses,
  clearSnapshots
} = snapshotsSlice.actions;

// Selectors
export const selectSnapshots = (state: { snapshots: SnapshotsState }) => 
  state.snapshots.snapshots;

export const selectSnapshotByKey = (key: string) => (state: { snapshots: SnapshotsState }) =>
  state.snapshots.snapshots[key];

export const selectIsStarred = (key: string) => (state: { snapshots: SnapshotsState }) =>
  !!state.snapshots.snapshots[key];

export const selectStarredCount = (state: { snapshots: SnapshotsState }) =>
  Object.keys(state.snapshots.snapshots).length;

export default snapshotsSlice.reducer;